'use client';

import React, { useEffect } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

import { workspaceModalSteps } from '@/config/contents';
import { DEFAULT_COMPANY_INFO, useCompanyInfo } from '@/hooks/use-company-info';
import { workspaceSchema } from '@/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { Session } from 'next-auth';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { activeWorkspaceAtom } from '../atoms';
import { useCreateWorkspaceModal } from '../store/use-create-workspace-modal';
import { WorkspaceDAO } from './create-workspace-modal';

export function CreateWorkspaceForm({
    session,
    suggestedDomain,
    suggestedName,
}: {
    session: Session | null;
    suggestedDomain: string | null;
    suggestedName: string | null;
}) {
    const queryClient = useQueryClient();
    const [, setWorkspace] = useAtom(activeWorkspaceAtom);
    const { setOpenWorkspaceCreateModal } = useCreateWorkspaceModal();

    const workspaceSubmit = useMutation({
        mutationFn: async (data: WorkspaceDAO) => {
            const response = await fetch('/api/workspace', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.error);
            return result;
        },
        onError: (error: Error) => {
            console.error('Error creating workspace:', error);
        },
        onSuccess: async (data) => {
            if (
                data.status === 'success' &&
                data.workspaceId &&
                data.workspace
            ) {
                await queryClient.invalidateQueries({
                    queryKey: ['workspaces', data.workspaceId],
                    exact: true,
                });
                setWorkspace(data.workspace);
            }
            setOpenWorkspaceCreateModal(false);
        },
    });

    const { data: companyInfo, isLoading: isCompanyInfoLoading } =
        useCompanyInfo(suggestedDomain) || {
            ...DEFAULT_COMPANY_INFO,
            name: suggestedName ?? '',
            domain: suggestedDomain ?? '',
        };

    // Update form initialization with schemas
    const workspaceForm = useForm<z.infer<typeof workspaceSchema>>({
        resolver: zodResolver(workspaceSchema),
        defaultValues: {
            name: suggestedName ?? '',
            domain: suggestedDomain ?? '',
            autoJoin: true,
            hasCompanyEmail: false,
        },
        mode: 'all',
    });

    useEffect(() => {
        if (companyInfo) {
            workspaceForm.reset({
                name:
                    companyInfo.name ?? companyInfo.domain?.split('@')[1] ?? '',
                domain: companyInfo.domain ?? '',
                autoJoin: companyInfo.hasCompanyEmail ?? false,
                hasCompanyEmail: companyInfo.hasCompanyEmail ?? false,
            });
        }
    }, [companyInfo, workspaceForm]);

    const onSubmit = async (e: React.FormEvent) => {
        console.log('onSubmit');
        e.preventDefault();
        if (workspaceSubmit.isPending) return; // Prevent multiple submissions

        const workspaceData = workspaceForm.getValues();

        workspaceSubmit.mutate({
            userId: session?.user?.id ?? '',
            workspace: workspaceData,
        });
    };

    return companyInfo && !isCompanyInfoLoading ? (
        companyInfo.hasCompanyEmail ? (
            createCompanyInfoForm({ workspaceForm, onSubmit })
        ) : (
            createPersonalInfoForm({ workspaceForm, onSubmit })
        )
    ) : (
        <div>Loading...</div>
    );
}

function createCompanyInfoForm({
    workspaceForm,
    onSubmit,
}: {
    workspaceForm: UseFormReturn<z.infer<typeof workspaceSchema>>;
    onSubmit: (e: React.FormEvent) => void;
}) {
    return (
        <Form {...workspaceForm}>
            <form
                onSubmit={onSubmit}
                className="flex flex-col h-full p-1"
                autoComplete="off"
            >
                <div className="flex-1 grid grid-cols-1 gap-6 mb-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                        <FormField
                            control={workspaceForm.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {
                                            workspaceModalSteps.workspaceCompanyNameLabel
                                        }
                                    </FormLabel>
                                    <FormControl>
                                        <Input placeholder="" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={workspaceForm.control}
                            name="domain"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {
                                            workspaceModalSteps.workspaceEmailDomainLabel
                                        }
                                    </FormLabel>
                                    <FormControl>
                                        <Input placeholder="" {...field} />
                                    </FormControl>
                                    <FormDescription>
                                        {
                                            workspaceModalSteps.workspaceEmailDomainDescription
                                        }
                                    </FormDescription>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                        <div className="space-y-0.5">
                            <FormLabel className="text-base">
                                Auto-join Members
                            </FormLabel>
                            <FormDescription>
                                New users with matching email domains are
                                automatically added to the workspace.
                            </FormDescription>
                        </div>
                    </div>
                </div>

                <div className="shrink-0">
                    <Button type="submit" className="w-full">
                        {workspaceModalSteps.workspaceCreateButton}
                    </Button>
                </div>
            </form>
        </Form>
    );
}

function createPersonalInfoForm({
    workspaceForm,
    onSubmit,
}: {
    workspaceForm: UseFormReturn<z.infer<typeof workspaceSchema>>;
    onSubmit: (e: React.FormEvent) => void;
}) {
    return (
        <Form {...workspaceForm}>
            <form
                onSubmit={onSubmit}
                className="flex flex-col h-full p-1"
                autoComplete="off"
            >
                <div className="flex-1 grid grid-cols-1 gap-8 mb-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                        <FormField
                            control={workspaceForm.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        {
                                            workspaceModalSteps.workspaceNonCompanyNameLabel
                                        }
                                    </FormLabel>
                                    <FormControl>
                                        <Input placeholder="" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>

                <div className="shrink-0">
                    <Button type="submit" className="w-full">
                        {workspaceModalSteps.workspaceCreateButton}
                    </Button>
                </div>
            </form>
        </Form>
    );
}
